import { truncate } from 'lodash'
import dayjs from 'shared/dayjs'
import { isUrl } from '../../utils/url'
import {
  EventRecurrenceSundayWeekDays,
  EventRecurrenceMondayWeekDays
} from 'shared/enums/recurringEvent'
import { EventRecurrenceDayEnum } from '../../graphql/private/PrivateSchemaTypes'
import { getWeekStartByLocale } from 'weekstart'
import { SupportedCamelCasedLocalesEnum } from 'types/SupportedLocalesEnum'
import { dayjsTz } from 'front-end/src/utils/dateHelpers'
import { apolloClientPublic } from 'front-end/src/graphql/apolloClient'
import {
  SessionDocument,
  SessionQuery
} from 'front-end/src/public-api/hooks/usePublicApiSession.codegen'
import { SupportedLocalesEnum } from 'front-end/src/graphql/public/PublicSchemaTypes'

export const formatEventLocation = (location: string | undefined | null) => {
  if (location) {
    if (isUrl(location)) {
      const locationObj = new URL(location)
      const loc = locationObj.origin || locationObj.hostname
      return loc.replace(/(^\w+:|^)\/\//, '')
    }
    return truncate(location, { length: 35 })
  }
  return null
}

export const formatTimeRange = (
  start_time: string | Date,
  end_time: string | Date,
  timezone?: string
) => {
  const start = timezone
    ? dayjsTz(start_time, timezone, true)
    : dayjs(start_time)
  const end = timezone ? dayjsTz(end_time, timezone, true) : dayjs(end_time)

  if (start.isSame(end, 'day')) {
    return `${start.format('LT')} - ${end.format('LT')}`
  } else {
    return `${start.format('lll')} - ${end.format('lll')}`
  }
}

export const isEventFull = (event) => {
  const attendees = getAttendees(event)
  let placesRemaining: null | number = null
  if (event.participants_limit && event.participants_limit > 0) {
    placesRemaining = event.participants_limit - attendees.length
  }

  return (
    event.participants_limit &&
    event.participants_limit > 0 &&
    placesRemaining !== null &&
    placesRemaining <= 0
  )
}

export const getAttendees = (event) => {
  const { attendedInvitees } = event
  return attendedInvitees.filter(({ attending }) => attending)
}

export const getDayIndexes = (days: EventRecurrenceDayEnum[]) => {
  const properLocaleWeek = getProperLocaleWeek()
  return days.map((day) => properLocaleWeek.findIndex((dd) => dd === day))
}

export const getDayValuesByIndex = (indexes: number[]) => {
  const properLocaleWeek = getProperLocaleWeek()
  return indexes.map((index) =>
    properLocaleWeek.find((_, i) => i === index)
  ) as EventRecurrenceDayEnum[]
}

export const getDaysInWeek = () => {
  return Array(7)
    .fill(0)
    .map((_, index) => dayjs().startOf('week').add(index, 'day').format('dddd'))
}

export const getDayFullNames = (days: EventRecurrenceDayEnum[]) => {
  const indexes = getDayIndexes(days)
  const options = getDaysInWeek()
  return indexes.map((day) => options[day])
}

export const getDayShortcut = (index: number) => {
  const options = getDaysInWeek()
  return options[index][0]
}

export const getProperLocaleWeek = () => {
  const loggedUser = apolloClientPublic.readQuery<SessionQuery>({
    query: SessionDocument,
    variables: {
      sid: null
    }
  })
  const locale = loggedUser?.session?.user.locale || SupportedLocalesEnum.enUs
  const languageCode = SupportedCamelCasedLocalesEnum[locale]
  return getWeekStartByLocale(languageCode)
    ? EventRecurrenceMondayWeekDays
    : EventRecurrenceSundayWeekDays
}
